import { Component, inject, OnInit } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { DocumentFilesService } from '@cl/core/services/document-files.service';
import { DocumentsService } from '@cl/core/services/documents.service';
import { DocumentFileInspection } from '@cl/shared/models/documents/document-file-inspection.model';
import { CommandResult } from '@cl/shared/models';
import { FileMetadata } from '@cl/shared/models/file.model';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';

@Component({
    selector: 'cl-document-file-preview-modal',
    standalone: false,
    templateUrl: './document-file-preview-modal.component.html',
    styleUrl: './document-file-preview-modal.component.less',
})
export class DocumentFilePreviewModalComponent implements OnInit {
    readonly previewData: {
        documentId: number;
        documentFileId: number;
        fileName: string;
        loadInspection?: () => Promise<CommandResult<DocumentFileInspection>>;
        loadFileMetadata?: (entryName?: string) => Promise<CommandResult<FileMetadata>>;
        downloadFile?: (entryName?: string) => Promise<void>;
    } = inject(NZ_MODAL_DATA);

    inspection: DocumentFileInspection | null = null;
    inspectionLoading = false;
    inspectionErrors: string[] = [];
    expanded = false;
    selectedEntryName?: string;
    downloadLoading = false;
    downloadErrors: string[] = [];

    constructor(
        private modal: NzModalRef,
        private documents: DocumentsService,
        private documentFiles: DocumentFilesService,
    ) {}

    // The viewer calls this for the file it currently shows: the original or the selected container entry.
    readonly loadFileMetadata = (): Promise<CommandResult<FileMetadata>> =>
        this.previewData.loadFileMetadata
            ? this.previewData.loadFileMetadata(this.selectedEntryName)
            : this.documents.getDocumentFileMetadata(this.previewData.documentId, this.previewData.documentFileId, this.selectedEntryName);

    ngOnInit(): void {
        void this.inspect();
    }

    close(): void {
        this.modal.destroy();
    }

    toggleExpanded(): void {
        this.expanded = !this.expanded;
        this.modal.updateConfig({
            nzWidth: this.expanded ? '98%' : '90%',
            nzStyle: { top: this.expanded ? '0.5rem' : '1rem', paddingBottom: this.expanded ? '0' : '1.5rem' },
        });
    }

    // Where the caller has no file name, the inspection result supplies the real one.
    get fileName(): string {
        return this.previewData.fileName || this.inspection?.fileName;
    }

    get isContainer(): boolean {
        return /\.(edoc|asice?|asics|sce|scs|zip)$/i.test(this.fileName || '') || !!this.inspection?.entries?.length;
    }

    get viewedFileName(): string {
        return this.selectedEntryName || this.fileName;
    }

    // A container lists its own files instead of the viewer's notice about an unsupported format.
    get showViewer(): boolean {
        return this.canPreview(this.viewedFileName) || (!this.inspectionLoading && !this.inspection?.entries?.length);
    }

    selectEntry(entryName: string): void {
        this.selectedEntryName = entryName;
    }

    canPreview(name: string): boolean {
        return /\.(pdf|png|jpe?g)$/i.test(name || '');
    }

    async download(entryName?: string): Promise<void> {
        this.downloadLoading = true;
        this.downloadErrors = [];
        try {
            if (this.previewData.downloadFile) {
                await this.previewData.downloadFile(entryName);
                return;
            }

            let params = new HttpParams().set('documentId', this.previewData.documentId).set('documentFileId', this.previewData.documentFileId);
            if (entryName !== undefined) {
                params = params.set('entryName', entryName);
            }

            await this.documents.downloadDocumentFile(params);
        } catch (error: any) {
            this.downloadErrors = this.errors(error, 'Failu neizdevās lejupielādēt.');
        } finally {
            this.downloadLoading = false;
        }
    }

    private async inspect(): Promise<void> {
        this.inspectionLoading = true;
        try {
            const result = this.previewData.loadInspection
                ? await this.previewData.loadInspection()
                : await this.documentFiles.inspectDocumentFile(this.previewData.documentId, this.previewData.documentFileId);
            if (result?.errors?.length || !result?.data) {
                throw { error: result };
            }

            this.inspection = result.data;
            if (!this.previewData.fileName && this.inspection.fileName) {
                this.modal.updateConfig({ nzTitle: this.inspection.fileName });
            }
        } catch (error: any) {
            this.inspectionErrors = this.errors(error, 'Parakstu informācija nav nosakāma');
        } finally {
            this.inspectionLoading = false;
        }
    }

    private errors(error: any, fallback: string): string[] {
        const errors = error.error?.errors;
        return Array.isArray(errors) && errors.length
            ? errors.map((message: any) => (typeof message === 'string' ? message : message.error || fallback))
            : [fallback];
    }
}
